import * as React from "react"

import { cn } from "@/lib/utils"

/*
  ProgressBar / ProgressRing — the kit's `.progress` bar and `.ring` (conic) meter, on theme tokens.

    <ProgressBar value={46} tone="lime" label="Invoiced" className="w-24" />
    <ProgressRing value={92} tone="lime" label="Execution" />          // "92%" in the middle
    <ProgressRing value={22} size={40} centerLabel="2/9" label="Stages approved" />

  value 0–100 (clamped). tone: navy | lime | green | amber | red | blue | violet | gray.
  ProgressBar props: value, tone, label (accessible name — say what it measures; default "Progress"),
  height (6), className.
  ProgressRing props: value, tone, size (40), thickness (5), centerLabel (default "N%"), label
  (accessible name, default "Progress"), className. Both are role="progressbar" with aria-valuenow.
*/

const FILL = {
  navy: "var(--brand)",
  lime: "var(--lime-600)",
  green: "var(--status-green)",
  amber: "var(--status-amber)",
  red: "var(--status-red)",
  blue: "var(--status-blue)",
  violet: "var(--status-violet)",
  gray: "var(--status-gray)",
}

function clamp(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return 0
  return Math.max(0, Math.min(100, number))
}

function ProgressBar({ value, tone = "navy", label = "Progress", height = 6, className }) {
  const pct = clamp(value)
  return (
    <span
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      className={cn("block min-w-10 overflow-hidden rounded-full bg-[color:var(--status-gray-bg)]", className)}
      style={{ height }}
    >
      <span className="block h-full rounded-full transition-[width]" style={{ width: `${pct}%`, background: FILL[tone] || FILL.navy }} />
    </span>
  )
}

function ProgressRing({ value, tone = "navy", size = 40, thickness = 5, centerLabel, label = "Progress", className }) {
  const pct = clamp(value)
  const text = centerLabel ?? `${Math.round(pct)}%`
  return (
    <span
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      className={cn("grid shrink-0 place-items-center rounded-full", className)}
      style={{
        width: size,
        height: size,
        background: `conic-gradient(${FILL[tone] || FILL.navy} ${pct * 3.6}deg, var(--status-gray-bg) 0)`,
      }}
    >
      <span
        className="grid place-items-center rounded-full bg-surface font-bold leading-none tabular-nums text-ink"
        style={{ width: size - thickness * 2, height: size - thickness * 2, fontSize: size >= 44 ? 11.5 : 10.5 }}
        aria-hidden="true"
      >
        {text}
      </span>
    </span>
  )
}

export { ProgressBar, ProgressRing }
